import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { getEventPublishedBundles } from './firebase';
import { normalizeMark, normalizeMarks } from './markNormalize';

/**
 * Load marks for a club event from WIP first, then latest published bundle.
 * Falls back to empty array if no marks found.
 * @param {string} clubId
 * @param {string} eventId
 * @returns {Promise<{marks: Array, source: string}>} marks array and source label
 */
export async function loadMarks(clubId, eventId) {
  // Try WIP first
  const wipRef = doc(db, 'clubs', clubId, 'events', eventId, 'wip', 'vNext');
  const wipSnap = await getDoc(wipRef);
  if (wipSnap.exists()) {
    const wip = wipSnap.data();
    if (wip.marks && wip.marks.length > 0) {
      // Normalize coords so any stored shape renders + is safe to re-save (ra-or1y).
      return { marks: normalizeMarks(wip.marks), source: 'wip' };
    }
  }

  // Fall back to latest published bundle
  const published = await getEventPublishedBundles(clubId, eventId);
  if (published.length > 0 && published[0].marks?.length > 0) {
    return { marks: normalizeMarks(published[0].marks), source: `published v${published[0].version || '?'}` };
  }

  return { marks: [], source: 'none' };
}

/**
 * Save the full marks array to the WIP document.
 * Uses merge so it doesn't overwrite course_cards or other WIP fields.
 * @param {string} clubId
 * @param {string} eventId
 * @param {Array} marks
 */
async function saveMarksToWip(clubId, eventId, marks) {
  const wipRef = doc(db, 'clubs', clubId, 'events', eventId, 'wip', 'vNext');
  await setDoc(wipRef, {
    club_id: clubId,
    event_id: eventId,
    marks,
    updated_at: serverTimestamp(),
  }, { merge: true });
}

/**
 * Save (create or update) a mark in the event WIP marks array.
 * @param {string} clubId
 * @param {string} eventId
 * @param {Object} mark - must include id, name, latitude, longitude
 */
export async function saveMark(clubId, eventId, mark) {
  const { marks } = await loadMarks(clubId, eventId);
  // Coerce coords to numbers (form inputs arrive as strings) + resolve the mark
  // code (id | mark_id) so lookup AND persistence use the same key (ra-or1y, codex).
  const norm = normalizeMark(mark);
  const idx = marks.findIndex((m) => m.id === norm.id);
  const entry = {
    id: norm.id,
    name: mark.name,
    latitude: norm.latitude,
    longitude: norm.longitude,
    type: mark.type || 'racing_mark',
    ...(mark.appearance ? { appearance: mark.appearance } : {}),
    ...(mark.lights ? { lights: mark.lights } : {}),
    ...(mark.vhf ? { vhf: mark.vhf } : {}),
    ...(mark.isLive != null ? { isLive: mark.isLive } : {}),
  };
  if (idx >= 0) {
    marks[idx] = entry;
  } else {
    marks.push(entry);
  }
  await saveMarksToWip(clubId, eventId, marks);
}

/**
 * Update specific fields on an existing mark in the WIP marks array.
 * @param {string} clubId
 * @param {string} eventId
 * @param {string} markId
 * @param {Object} fields
 */
export async function updateMark(clubId, eventId, markId, fields) {
  const { marks } = await loadMarks(clubId, eventId);
  const idx = marks.findIndex((m) => m.id === markId);
  if (idx < 0) throw new Error(`Mark "${markId}" not found`);
  marks[idx] = { ...marks[idx], ...fields };
  await saveMarksToWip(clubId, eventId, marks);
}

/**
 * Delete a mark from the WIP marks array.
 * @param {string} clubId
 * @param {string} eventId
 * @param {string} markId
 */
export async function deleteMark(clubId, eventId, markId) {
  const { marks } = await loadMarks(clubId, eventId);
  const filtered = marks.filter((m) => m.id !== markId);
  await saveMarksToWip(clubId, eventId, filtered);
}
